import { ComponentsHeader } from '@primitive/sectionHeadings'
import { Button, Container, Empty, Flex, Input, Loader, ToolTip } from '@ui/index'
import { IconDelete, IconEdit, IconEye, IconGlobal, IconPassword, IconProfile } from '@img/icons'
import imgEmpty from '@img/empty.svg'
import avatar from '@img/avatar_1.png'
import logoModSkill from '@img/logo_modskill.svg'
import logoYaSkill from '@img/logo_yaskill.svg'

import { IProfileUI } from './types'
import css from './ui.module.scss'

const getOperatorName = (lastName: string, firstName: string, secondName: string) => {
    const fullName = `${lastName} ${firstName} ${secondName}`.trim()
    return fullName || 'Оператор'
}

const getLogoSite = (dbName: string) => {
    switch (dbName) {
        case 'modern_academy': return logoModSkill
        case 'yaskill': return logoYaSkill
        default: return undefined
    }
}

const getAccessIcon = (type: 'read' | 'edit' | 'delete') => {
    switch (type) {
        case 'read': return <IconEye width="17px" height="17px" />
        case 'edit': return <IconEdit width="17px" height="17px" />
        case 'delete': return <IconDelete width="17px" height="17px" />
    }
}

const AccessBadge = ({ isActive, type }: { isActive: boolean, type: 'read' | 'edit' | 'delete' }) => (
    <span className={isActive ? `${css.badge} ${css.badge_active}` : css.badge}>{getAccessIcon(type)}</span>
)

/** UI - Мой профиль */
export const ProfileUI = ({
    isLoadingProfile,
    isLoadingPassword,
    profile,
    profileSites,
    profileAccess,
    txtCurrentPassword,
    txtNewPassword,
    txtConfirmPassword,
    handleLoadProfile,
    handleChangePassword,
}: IProfileUI) => {
    const operatorName = getOperatorName(profile.lastName, profile.firstName, profile.secondName)

    return <div className={css.container}>

        <ComponentsHeader
            headerIcon={<IconProfile width="20px" height="20px" />}
            headerTitle="Мой профиль"
            btnUpdateOnClick={handleLoadProfile}
        />

        <div className={css.loading_region}>
            <Loader isOpen={isLoadingProfile} backdrop title="Загрузка..." />
            <div className={css.content}>
            <div className={css.top_grid}>
                <Container className={css.profile_card} padding="12px" overflow="visible">
                    <Flex flexDirection="column" gap="12px">
                        <div className={css.profile_head}>
                            <div className={css.avatar}>
                                <img src={avatar} alt="Аватар оператора" />
                            </div>

                            <div className={css.profile_title}>
                                <h3>{operatorName}</h3>
                                <p>{profile.loginName}</p>
                            </div>
                        </div>

                        <div className={css.info_grid}>
                            <div className={css.info_item}>
                                <p>Уровень доступа</p>
                                <b>{profile.accessLevelName || '-'}</b>
                            </div>

                            <div className={css.info_item}>
                                <p>Статус</p>
                                <b>{profile.isLocked === 1 ? 'Заблокирован' : 'Активен'}</b>
                            </div>

                            <div className={css.info_item}>
                                <p>Доступно сайтов</p>
                                <b>{profileSites.length}</b>
                            </div>

                            <div className={css.info_item}>
                                <p>Доступно разделов</p>
                                <b>{profileAccess.length}</b>
                            </div>
                        </div>
                    </Flex>
                </Container>

                <Container className={css.password_card} padding="12px" overflow="visible">
                    <Flex flexDirection="column" gap="10px">
                        <div className={css.block_title}>
                            <IconPassword width="20px" height="20px" />
                            <h4>Смена пароля</h4>
                        </div>

                        <Input type="password" placeholder="Текущий пароль" autoComplete="current-password" {...txtCurrentPassword} />
                        <Input type="password" placeholder="Новый пароль" autoComplete="new-password" {...txtNewPassword} />
                        <Input type="password" placeholder="Повтор нового пароля" autoComplete="new-password" {...txtConfirmPassword} />

                        <Button padding="0 14px" isLoading={isLoadingPassword} isDisabled={isLoadingPassword} onClick={handleChangePassword}>
                            Изменить пароль
                        </Button>
                    </Flex>
                </Container>
            </div>

            <div className={css.sections_grid}>
                <Container className={css.section_card} padding="12px" overflow="hidden">
                    <div className={css.block_title}>
                        <IconGlobal width="20px" height="20px" />
                        <h4>Доступные сайты</h4>
                    </div>

                    <div className={css.list}>
                        {profileSites.length === 0
                            ? <Empty image={imgEmpty} title="Данные отсутствуют" />
                            : profileSites.map((item) => {
                                const siteLogo = getLogoSite(item.siteDBName)

                                return (
                                    <div className={css.site_row} key={item.loginSiteID}>
                                        <div className={css.site_logo}>
                                            {siteLogo
                                                ? <img src={siteLogo} alt="Лого сайта" />
                                                : <IconProfile width="24px" height="24px" />
                                            }
                                        </div>

                                        <div className={css.site_text}>
                                            <b>{item.siteDbNameDescription}</b>
                                            <p>{item.siteLink}</p>
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </div>
                </Container>

                <Container className={css.section_card} padding="12px" overflow="hidden">
                    <div className={css.block_title}>
                        <IconProfile width="20px" height="20px" />
                        <h4>Доступные разделы</h4>
                    </div>

                    <div className={css.access_list}>
                        {profileAccess.length === 0
                            ? <Empty image={imgEmpty} title="Данные отсутствуют" />
                            : profileAccess.map((item) => (
                                <div className={css.access_row} key={item.accessID}>
                                    <div className={css.access_text}>
                                        <b>{item.accessSectionName}</b>
                                        <p>{item.accessSectionDescription}</p>
                                    </div>

                                    <div className={css.badges}>
                                        <ToolTip title="Чтение" placement="top">
                                            <AccessBadge isActive={item.isRead === 1} type="read" />
                                        </ToolTip>
                                        <ToolTip title="Редактирование" placement="top">
                                            <AccessBadge isActive={item.isEdit === 1} type="edit" />
                                        </ToolTip>
                                        <ToolTip title="Удаление" placement="top">
                                            <AccessBadge isActive={item.isDelete === 1} type="delete" />
                                        </ToolTip>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                </Container>
            </div>
            </div>
        </div>
    </div>
}